import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScatterPlotRoundedIcon from "@mui/icons-material/ScatterPlotRounded";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { AnalysisOptionCard } from "./saanvi-styles";

export default function AnalysisModal({
  open,
  onClose,
  deepAnalysis,
  deepDive,
  deeperDive,
  loading,
  onDeepAnalysisToggle,
  onDeepDiveToggle,
  onDeeperDiveToggle,
}) {
  const theme = useTheme();

  const handleSelect = (toggleFn) => {
    if (!loading) {
      toggleFn();
      setTimeout(() => onClose(), 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        borderColor: "transparent",
        elevation: 0,
        sx: {
          borderRadius: "20px",
          p: 2,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
      }}
      Backdrop={{
        sx: {
          backdropFilter: "blur(15px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "text.secondary",
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            mb: 3,
          }}
        >
          Analysis Options
        </Typography>

        <Stack spacing={2}>
          {/* Deep Analysis Option */}
          <AnalysisOptionCard
            isSelected={deepAnalysis}
            onClick={() => handleSelect(onDeepAnalysisToggle)}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: "8px",
                  background: deepAnalysis
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.2),
                  color: deepAnalysis ? "white" : theme.palette.primary.main,
                }}
              >
                <ScatterPlotRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: deepAnalysis ? "primary.main" : "text.primary",
                  }}
                >
                  Deep Analysis
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.text.secondary, 0.7),
                    fontSize: "13px",
                  }}
                >
                  Get comprehensive analysis with detailed insights
                </Typography>
              </Box>
            </Stack>
          </AnalysisOptionCard>

          {/* Deep Dive Option */}
          <AnalysisOptionCard
            isSelected={deepDive}
            onClick={() => handleSelect(onDeepDiveToggle)}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: "8px",
                  background: deepDive
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.2),
                  color: deepDive ? "white" : theme.palette.primary.main,
                }}
              >
                <BubbleChartRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: deepDive ? "primary.main" : "text.primary",
                  }}
                >
                  Deep Dive
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.text.secondary, 0.7),
                    fontSize: "13px",
                  }}
                >
                  Explore topics in extreme detail with multiple perspectives
                </Typography>
              </Box>
            </Stack>
          </AnalysisOptionCard>

          {/* Deeper Dive Option */}
          <AnalysisOptionCard
            isSelected={deeperDive}
            onClick={() => handleSelect(onDeeperDiveToggle)}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: "8px",
                  background: deeperDive
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.2),
                  color: deeperDive ? "white" : theme.palette.primary.main,
                }}
              >
                <TravelExploreIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: deeperDive ? "primary.main" : "text.primary",
                  }}
                >
                  Deeper Dive
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.text.secondary, 0.7),
                    fontSize: "13px",
                  }}
                >
                  Ultra-comprehensive research with extensive analysis
                </Typography>
              </Box>
            </Stack>
          </AnalysisOptionCard>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
